import React from 'react'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSelector } from 'react-redux'
import { Icon } from '@src/components/base'
import { CustomEventTypes } from '@config'
import { AppStackParamList } from '@src/navigation/types'
import { AppointmentOffer } from '@src/screens/UpsellOffer/AppointmentOffer'
import { CoachesCoachingKind, EhrAppointmentAttendanceChargeKind } from '@src/types'
import { getCoachingKindDescriptor } from '@src/utils/coach'
import { useGoBack } from '@src/utils'
import { priorityOrderedCoachAssignmentsSelector } from '../models/nutritionistHub.selectors'
import { useNutritionistHubTracker } from '../hooks'
import { useCallSchedulingState } from './NextCall/useCallSchedulingState'
import { scheduleMntVideoCall } from './ScheduleNextCall/useScheduleCoachVideoCall'

const benefits = [
  '1:1 sessions with nutrition experts',
  'Fewer cravings, less guesswork',
  'Simple tools that fit your lifestyle',
  'Proven outcomes: healthier habits, fewer cravings, better energy',
] as const

export const VideoCallsGettingStartedScreen = () => {
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const goBack = useGoBack()
  const track = useNutritionistHubTracker()
  const coachAssignments = useSelector(priorityOrderedCoachAssignmentsSelector) || []

  const { schedulingInfo, loading } = useCallSchedulingState({ only: CoachesCoachingKind.Mnt })
  const mnt = schedulingInfo?.mnt
  const defaultOption = mnt?.options[0]
  const chargeKind = mnt?.chargeKind
  const mntCoachAssignment = coachAssignments.find(
    (assignment) => assignment.coachingKind.key === CoachesCoachingKind.Mnt,
  )
  const hasMntCoach = !!mntCoachAssignment?.coach
  const coachDescriptor = getCoachingKindDescriptor(CoachesCoachingKind.Mnt)
  const coachTitle = coachDescriptor.capitalized.toString()
  const coachPluralTitle = coachDescriptor.capitalized.plural.toString()

  const handleContinue = () => {
    track(CustomEventTypes.NutritionistHubAddInsuranceTapped)
    scheduleMntVideoCall({
      mnt,
      navigation,
      navigateToGettingStartedForUndetermined: false,
    })
  }

  const handleDismiss = () => {
    if (!defaultOption) {
      goBack()
      return
    }

    track(CustomEventTypes.NutritionistHubBookWithoutInsuranceTapped)

    if (chargeKind === EhrAppointmentAttendanceChargeKind.BsSelfPay) {
      const { appointmentKind, payAsYouGoProduct } = defaultOption

      if (!hasMntCoach) {
        navigation.navigate('CallSchedulingSeeAllCoachesScreen', {
          coachingKind: CoachesCoachingKind.Mnt,
          navigationTitle: `Choose Your ${coachTitle}`,
          title: `Available ${coachPluralTitle}`,
          loadMoreButtonText: 'Load More',
          showFilters: true,
          callSchedulingParams: {
            appointmentKind,
            allowBackNavigation: true,
            product: payAsYouGoProduct,
          },
        })
        return
      }

      // Skip checkout in the unified flow; confirmation and pricing will appear after call scheduling.
      navigation.navigate('CallScheduling', {
        appointmentKind,
        allowBackNavigation: true,
        product: payAsYouGoProduct,
      })
      return
    }

    navigation.navigate('VideoCallCheckout')
  }

  return (
    <AppointmentOffer
      coachingKind="nutrition"
      data={{
        title: 'Nutrition\nCoaching',
        description:
          'Get personalized nutrition guidance based on your body, goals, and preferences',
        benefits,
        note: 'Most pay $0 out-of-pocket',
        primaryActionText: 'Book My Session',
        secondaryActionText: 'No thanks',
      }}
      onPrimaryPress={handleContinue}
      onSkipPress={handleDismiss}
      onClosePress={goBack}
      secondaryButtonDisabled={loading}
      primaryButtonAccessoryRight={<Icon name="arrow-right" weight="bold" />}
    />
  )
}
